import type { UserInfo } from '@/api/auth';
import { getDefaultRedirect } from '@/hooks/useAuth';

const ADMIN_ACCESS_TOKEN_KEY = 'admin_access_token';
const ADMIN_REFRESH_TOKEN_KEY = 'admin_refresh_token';
const IMPERSONATED_USER_KEY = 'impersonated_user';

export function useImpersonation() {
  const raw = localStorage.getItem(IMPERSONATED_USER_KEY);
  const impersonatedUser: UserInfo | null = raw ? (JSON.parse(raw) as UserInfo) : null;
  const isImpersonating = !!impersonatedUser;

  function startImpersonation(token: string, user: UserInfo) {
    // Save current admin tokens
    const currentToken = localStorage.getItem('access_token');
    if (currentToken) {
      localStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, currentToken);
    }
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken);
    }

    // Swap in impersonation token (no refresh token during impersonation)
    localStorage.setItem('access_token', token);
    localStorage.removeItem('refresh_token');
    localStorage.setItem(IMPERSONATED_USER_KEY, JSON.stringify(user));

    // Clear stale academy context so the new session resolves fresh
    sessionStorage.removeItem('academy_id');

    // Redirect to the default page for the impersonated user's roles
    // getDefaultRedirect returns router-relative paths; prepend /assess for window.location
    window.location.href = '/assess' + getDefaultRedirect(user.roles);
  }

  function stopImpersonation() {
    const adminToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    const adminRefresh = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);

    if (adminToken) {
      localStorage.setItem('access_token', adminToken);
    }
    if (adminRefresh) {
      localStorage.setItem('refresh_token', adminRefresh);
    }

    localStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATED_USER_KEY);
    sessionStorage.removeItem('academy_id');

    window.location.href = '/assess/admin';
  }

  return { isImpersonating, impersonatedUser, startImpersonation, stopImpersonation };
}
